import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatErrorDetail = (detail) => {
    if (detail == null) return "Errore. Riprova tra qualche secondo.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

export const AuthProvider = ({ children }) => {
    // null = checking, false = anonymous, object = admin
    const [user, setUser] = useState(null);

    const checkSession = useCallback(async () => {
        try {
            const resp = await fetch(`${API}/auth/me`, { credentials: "include" });
            if (resp.ok) {
                setUser(await resp.json());
            } else {
                setUser(false);
            }
        } catch (_err) {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    const login = useCallback(async (email, password) => {
        const resp = await fetch(`${API}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ email, password }),
        });
        if (!resp.ok) {
            const body = await resp.json().catch(() => ({}));
            throw new Error(formatErrorDetail(body.detail));
        }
        const data = await resp.json();
        setUser(data);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
        } catch (err) {
            // Network failure on logout: we still clear local state.
            // Log in dev so it shows up during debugging.
            if (process.env.NODE_ENV !== "production") {
                console.warn("[Auth] logout request failed:", err);
            }
        }
        setUser(false);
    }, []);

    const value = useMemo(
        () => ({ user, login, logout, refresh: checkSession }),
        [user, login, logout, checkSession]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
